import { useMemo } from 'react'

import useWindowSize from '@/hooks/useWindowSize'
import { MessageSource } from '@/ui/components/MessageSource/MessageSource'
import MessageSourceGroup from '@/ui/components/MessageSource/MessageSourceGroup'
import { MessageSourceConfigItem, MessageSourcesProps } from './types/MessageSourcesTypes'

import './MessageSources.scss'

const sourceConfig: Record<'mobile' | 'desktop', MessageSourceConfigItem[]> = {
    mobile: [{ key: 'line' }, { key: 'origin' }],
    desktop: [
        { key: 'line', prefix: 'Line' },
        { key: 'origin', prefix: 'Source' },
    ],
}

const MessageSources = ({ message }: MessageSourcesProps) => {
    const { isMobile } = useWindowSize()

    const messageSourcesContent = useMemo(() => {
        const messageSources = message.sources

        const showSources =
            messageSources &&
            messageSources.length > 0

        if (!showSources) {
            return null
        }

        // Group sources by cluster_key strategy, then legacy clusterType, then origin::line
        const sourceGroups = new Map<string, typeof messageSources>()
        
        messageSources.forEach((source) => {
            let groupKey: string

            if (source.cluster_key === 'origin') groupKey = source.origin ?? 'N/A'
            else if (source.cluster_key === 'line') groupKey = source.line ?? 'N/A'
            else groupKey = source.clusterType ?? `${source.origin ?? ''}::${source.line ?? ''}`
            
            if (!sourceGroups.has(groupKey)) {
                sourceGroups.set(groupKey, [])
            }
            sourceGroups.get(groupKey)!.push(source)
        })

        const config = isMobile ? sourceConfig.mobile : sourceConfig.desktop

        return (
            <div className="source-section">
                {Array.from(sourceGroups.entries()).map(([groupKey, groupSources]) => (
                    groupSources.length === 1 ? (
                        // Single-source groups render directly without grouping UI
                        <MessageSource
                            key={`${String(groupSources[0].link)}-${groupKey}`}
                            source={groupSources[0]}
                            config={config}
                        />
                    ) : (
                        // Multi-source groups render with grouping UI and badge
                        <MessageSourceGroup
                            key={groupKey}
                            groupKey={groupKey}
                            sources={groupSources}
                            config={config}
                        />
                    )
                ))}
            </div>
        )
        // finishedMessage is not read: the sources are grouped again when the answer is finished, as they can change in place
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, message.finishedMessage, message.sources])

    return <>{messageSourcesContent}</>
}

export default MessageSources
